import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import './index.css';
import { initializeRemoteLogger } from '@/lib/remote-logger';
import type { SsrBootstrapState } from '@/lib/ssr-state';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

const ensureTypographyClasses = () => {
  if (!isBrowser || document.getElementById('app-typography-classes')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'app-typography-classes';
  style.textContent = `
    .prose h1, .prose h2, .prose h3, .prose h4 {
      font-weight: 600;
      margin-top: 1.5em;
      margin-bottom: 0.5em;
    }
    .prose strong {
      font-weight: 600;
    }
    .prose p {
      margin-bottom: 1em;
    }
    .prose ol, .prose ul {
      margin-top: 0.5em;
      margin-bottom: 1em;
      padding-left: 1.5em;
    }
    .prose li {
      margin-bottom: 0.5em;
    }
    .prose a {
      color: #3b82f6;
      text-decoration: underline;
    }
    .dark .prose a {
      color: #60a5fa;
    }
    .prose blockquote {
      border-left: 3px solid #d1d5db;
      padding-left: 1em;
      font-style: italic;
    }
    .dark .prose blockquote {
      border-left-color: #4b5563;
    }
  `;
  document.head.appendChild(style);
};

const PWA_CLEANUP_KEY = 'canxp-legacy-pwa-cleanup-v1';

const cleanupLegacyPwa = async () => {
  if (!isBrowser || window.localStorage.getItem(PWA_CLEANUP_KEY) === 'done') {
    return;
  }

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if ('caches' in window) {
      const cacheKeys = await window.caches.keys();
      await Promise.all(cacheKeys.map((cacheKey) => window.caches.delete(cacheKey)));
    }

    window.localStorage.setItem(PWA_CLEANUP_KEY, 'done');
  } catch (error) {
    console.warn('[PWA cleanup] Failed to clear legacy caches:', error);
  }
};

if (isBrowser) {

  initializeRemoteLogger();
  ensureTypographyClasses();
  void cleanupLegacyPwa();

  if (window.location.pathname === '/') {
    document.documentElement.classList.add('allow-window-scroll');
  }

  const rootElement = document.getElementById('root');
  const ssrState: SsrBootstrapState | undefined = window.__CANXP_SSR__;

  if (rootElement) {
    const app = (
      <BrowserRouter>
        <HelmetProvider>
          <App initialAuthState={ssrState?.auth} />
        </HelmetProvider>
      </BrowserRouter>
    );

    if (rootElement.hasChildNodes()) {
      hydrateRoot(rootElement, app);
    } else {
      createRoot(rootElement).render(app);
    }
  }
}